import React from "react";
import {
    Box,
    makeStyles,
    Tabs,
    Typography,
    useTheme,
    Divider,
    Button,
    Grid,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
} from "@material-ui/core";
import moment from "moment";
import CustomTab from "components/tab";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import { Wrapper } from "modules/reporting/components/layout";
import {
    BarChart,
    LineChart,
} from "modules/reporting/components/charts";
import { StatCard } from "./components";
import SwipeableViews from "react-swipeable-views";
import CreditMovementContainer from "../credit-movement/credit-movement.container";
import GetAppIcon from "@material-ui/icons/GetApp";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PrintIcon from "@material-ui/icons/Print";
import AssessmentIcon from "@material-ui/icons/Assessment";
import {QcImpactAnalysisPage} from "../qc-impact-analysis";

const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        backgroundColor: "#F9FAFB",
        minHeight: "100vh",
    },
    header__wrapper: {
        backgroundColor: "#FFFFFF",
        paddingTop: "16px",
        paddingLeft: "16px",
        paddingRight: "16px",
    },
    header__titleRow: {
        display: "flex",
        alignItems: "flex-end",
        fontWeight: "bold",
        marginBottom: "8px",
    },
    indicator: {
        border: "0.5px solid",
        borderColor: theme.palette.primary.main,
    },
    scroll: {
        width: "min-content",
    },
    content: {
        padding: "24px 32px",
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    },
    sectionTitle: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#101828",
    },
    actionButtons: {
        display: "flex",
        gap: "12px",
    },
    filterButton: {
        textTransform: "none",
        borderColor: "#D0D5DD",
        color: "#344054",
        fontWeight: 500,
    },
    monthSelect: {
        minWidth: "120px",
        height: "40px",
        "& .MuiSelect-root": {
            padding: "8px 14px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D0D5DD",
        },
    },
    yearSelect: {
        minWidth: "100px",
        height: "40px",
        "& .MuiSelect-root": {
            padding: "8px 14px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#D0D5DD",
        },
    },
    filterGroup: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    exportButton: {
        textTransform: "none",
        backgroundColor: "#3C4E54",
        color: "#FFFFFF",
        fontWeight: 500,
        "&:hover": {
            backgroundColor: "#2E3E44",
        },
    },
    statsRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "24px",
        flexWrap: "wrap",
    },
    chartsRow: {
        display: "flex",
        gap: "16px",
        marginBottom: "24px",
    },
    chartCard: {
        flex: 1,
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E4E7EC",
        padding: "20px",
    },
    chartHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    },
    chartTitle: {
        fontSize: "14px",
        fontWeight: 500,
        color: "#101828",
    },
    fullWidthChartCard: {
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E4E7EC",
        padding: "20px",
        marginBottom: "24px",
    },
    periodToggle: {
        display: "flex",
        backgroundColor: "#F2F4F7",
        borderRadius: "8px",
        padding: "4px",
    },
    periodButton: {
        textTransform: "none",
        padding: "6px 12px",
        minWidth: "auto",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 500,
        color: "#667085",
    },
    periodButtonActive: {
        backgroundColor: "#FFFFFF",
        color: "#344054",
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
    },
    fullWidth: {
        width: "100%",
        margin: "0px",
    },
    legendContainer: {
        display: "flex",
        gap: "24px",
        marginBottom: "16px",
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    legendDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
    },
    legendText: {
        fontSize: "12px",
        color: "#667085",
    },
    loadingOverlay: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        gap: "16px",
    },
    loadingText: {
        fontSize: "14px",
        color: "#667085",
    },
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`credit-tabpanel-${index}`}
            aria-labelledby={`credit-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

export default function CreditReportingPage({
    executiveOverview,
    monthlyUsageData,
    creditsUsedVsReimbursedData,
    printedVsRequestedData,
    activeTab,
    periodFilter,
    selectedMonth,
    onTabChange,
    onPeriodFilterChange,
    onMonthChange,
    onExport,
    translate,
    language,
}) {
    const classes = useStyles();
    const theme = useTheme();

    // Generate month options (0-11)
    const monthOptions = moment.months().map((name, index) => ({
        value: index,
        label: name,
    }));

    // Generate year options (current year and 5 years back)
    const currentYear = moment().year();
    const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

    const handleMonthSelect = (event) => {
        if (onMonthChange) {
            onMonthChange({
                ...selectedMonth,
                month: event.target.value,
            });
        }
    };

    const handleYearSelect = (event) => {
        if (onMonthChange) {
            onMonthChange({
                ...selectedMonth,
                year: event.target.value,
            });
        }
    };

    const tabLabels = [
        translate("label.EXECUTIVE_OVERVIEW"),
        translate("label.CREDIT_MOVEMENT"),
        translate("label.QC_IMPACT_ANALYSIS"),
    ];

    function a11yProps(index, label = "label") {
        return {
            id: `scrollable-auto-tab-${index}`,
            "aria-controls": `scrollable-auto-tab-${index}`,
            label: <Typography variant="button" children={label} />,
        };
    }

    const handleTabChange = (event, newValue) => {
        if (onTabChange) {
            onTabChange(newValue);
        }
    };

    const renderExecutiveOverview = () => {
        // Show loading state while data is being fetched
        const isLoading = executiveOverview?.isLoading &&
            !executiveOverview?.isSuccess &&
            executiveOverview?.openingBalance === 0;

        if (isLoading) {
            return (
                <Box className={classes.content}>
                    <Box className={classes.loadingOverlay}>
                        <CircularProgress size={48} color="primary" />
                        <Typography className={classes.loadingText}>
                            {translate("label.LOADING")}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        return (
            <Box className={classes.content}>
                {/* Section Header */}
                <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                    {translate("label.EXECUTIVE_OVERVIEW")}
                </Typography>
                <Box className={classes.actionButtons}>
                    <Box className={classes.filterGroup}>
                        <FormControl variant="outlined" size="small">
                            <Select
                                value={selectedMonth?.month ?? moment().month()}
                                onChange={handleMonthSelect}
                                className={classes.monthSelect}
                            >
                                {monthOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" size="small">
                            <Select
                                value={selectedMonth?.year ?? moment().year()}
                                onChange={handleYearSelect}
                                className={classes.yearSelect}
                            >
                                {yearOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Button
                        variant="contained"
                        className={classes.exportButton}
                        startIcon={<GetAppIcon />}
                        onClick={onExport}
                    >
                        {translate("label.EXPORT")}
                    </Button>
                </Box>
            </Box>

            {/* Stats Row 1 */}
            <Box className={classes.statsRow}>
                <StatCard
                    icon={<AccountBalanceWalletIcon />}
                    iconBgColor="#F0F9FF"
                    iconColor="#0BA5EC"
                    label={translate("label.OPENING_BALANCE")}
                    value={executiveOverview?.openingBalance || 0}
                    subLabel={translate("label.CREDITS_AVAILABLE")}
                    change={executiveOverview?.openingBalanceChange}
                />
                <StatCard
                    icon={<CreditCardIcon />}
                    iconBgColor="#FFF4ED"
                    iconColor="#FB6514"
                    label={translate("label.ALLOCATED_CREDITS")}
                    value={executiveOverview?.allocatedCredits || 0}
                    subLabel={translate("label.STANDARD_ALLOCATION")}
                    showPeriod
                    periodLabel={translate("label.MONTHLY")}
                />
                <StatCard
                    icon={<TrendingDownIcon />}
                    iconBgColor="#FEF3F2"
                    iconColor="#F04438"
                    label={translate("label.CREDITS_USED")}
                    value={executiveOverview?.creditsUsed || 0}
                    subLabel={translate("label.FROM_PRINT_JOBS")}
                    change={executiveOverview?.creditsUsedChange}
                />
                <StatCard
                    icon={<TrendingUpIcon />}
                    iconBgColor="#ECFDF3"
                    iconColor="#12B76A"
                    label={translate("label.CREDITS_REIMBURSED")}
                    value={executiveOverview?.creditsReimbursed || 0}
                    subLabel={translate("label.FROM_QC_RETURNS")}
                    change={executiveOverview?.creditsReimbursedChange}
                />
            </Box>

            {/* Stats Row 2 */}
            <Box className={classes.statsRow}>
                <StatCard
                    icon={<AccountBalanceWalletIcon />}
                    iconBgColor={theme.palette.primary.main}
                    iconColor="#FFFFFF"
                    label={translate("label.CLOSING_BALANCE")}
                    value={executiveOverview?.closingBalance || 0}
                    subLabel={translate("label.AVAILABLE_FOR_NEXT_PERIOD")}
                    highlighted
                />
                <StatCard
                    icon={<CreditCardIcon />}
                    iconBgColor="#FFF4ED"
                    iconColor="#FB6514"
                    label={translate("label.ELIGIBLE_ROLLOVER")}
                    value={executiveOverview?.eligibleRollover || 0}
                    subLabel={translate("label.OF_CLOSING_BALANCE")}
                />
                <StatCard
                    icon={<PrintIcon />}
                    iconBgColor="#EFF8FF"
                    iconColor="#2E90FA"
                    label={translate("label.TOTAL_PRINT_JOBS")}
                    value={executiveOverview?.totalPrintJobs || 0}
                    subLabel={translate("label.COMPLETED_THIS_MONTH")}
                />
                <StatCard
                    icon={<AssessmentIcon />}
                    iconBgColor="#F4F3FF"
                    iconColor="#7A5AF8"
                    label={translate("label.UTILIZATION_RATE")}
                    value={String(executiveOverview?.utilizationRate || 0).includes('%')
                        ? executiveOverview?.utilizationRate
                        : `${executiveOverview?.utilizationRate || 0}%`}
                    subLabel={translate("label.OF_ALLOCATED_CREDITS")}
                />
            </Box>

            {/* Charts Row */}
            <Grid container spacing={2} style={{ marginBottom: "24px" }}>
                <Grid item xs={12} md={6}>
                    <Box className={classes.chartCard}>
                        <Box className={classes.chartHeader}>
                            <Typography className={classes.chartTitle}>
                                {translate("label.MONTHLY_CREDIT_USAGE")}
                            </Typography>
                        </Box>
                        {monthlyUsageData?.datasets?.length > 0 ? (
                            <BarChart
                                payload={monthlyUsageData}
                                chartHeight={300}
                                chartColors={["#0BA5EC"]}
                                translate={translate}
                            />
                        ) : (
                            <Box style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography color="textSecondary">
                                    {translate("label.NO_DATA")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box className={classes.chartCard}>
                        <Box className={classes.chartHeader}>
                            <Typography className={classes.chartTitle}>
                                {translate("label.CREDITS_USED_VS_REIMBURSED")}
                            </Typography>
                            <Box className={classes.legendContainer}>
                                <Box className={classes.legendItem}>
                                    <Box className={classes.legendDot} style={{ backgroundColor: "#F04438" }} />
                                    <Typography className={classes.legendText}>
                                        {translate("label.CREDITS_USED")}
                                    </Typography>
                                </Box>
                                <Box className={classes.legendItem}>
                                    <Box className={classes.legendDot} style={{ backgroundColor: "#12B76A" }} />
                                    <Typography className={classes.legendText}>
                                        {translate("label.CREDITS_REIMBURSED")}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {creditsUsedVsReimbursedData?.datasets?.length > 0 ? (
                            <BarChart
                                payload={creditsUsedVsReimbursedData}
                                chartHeight={300}
                                chartColors={["#F04438", "#12B76A"]}
                                translate={translate}
                            />
                        ) : (
                            <Box style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Typography color="textSecondary">
                                    {translate("label.NO_DATA")}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Full Width Line Chart */}
            <Box className={classes.fullWidthChartCard}>
                <Box className={classes.chartHeader}>
                    <Box>
                        <Typography className={classes.chartTitle}>
                            {translate("label.PRINTED_VS_REQUESTED_QUANTITY")}
                        </Typography>
                        <Box className={classes.legendContainer} style={{ marginTop: "8px" }}>
                            <Box className={classes.legendItem}>
                                <Box className={classes.legendDot} style={{ backgroundColor: "#0BA5EC" }} />
                                <Typography className={classes.legendText}>
                                    {translate("label.REQUESTED_QUANTITY")}
                                </Typography>
                            </Box>
                            <Box className={classes.legendItem}>
                                <Box className={classes.legendDot} style={{ backgroundColor: "#12B76A" }} />
                                <Typography className={classes.legendText}>
                                    {translate("label.PRINTED_QUANTITY")}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {printedVsRequestedData?.datasets?.length > 0 ? (
                    <LineChart
                        payload={printedVsRequestedData}
                        chartHeight={350}
                        language={language}
                        chartColors={["#0BA5EC", "#12B76A"]}
                        translate={translate}
                    />
                ) : (
                    <Box style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography color="textSecondary">
                            {translate("label.NO_DATA")}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
        );
    };

    const renderCreditMovement = () => (
        <CreditMovementContainer translate={translate} language={language} />
    );

    const renderQCImpactAnalysis = () => (
        <QcImpactAnalysisPage />
    );

    return (
        <Wrapper>
            <Box className={classes.pageWrapper}>
                {/* Header */}
                <Box gridArea="topbar">
                    <Box
                        mb={1}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        px={3}
                        pt={3}
                    >
                        <Box display="flex" alignItems="flex-end" fontWeight="bold">
                            <Typography variant="h6" color="primary">
                                {translate("label.QR_CREDIT_REPORTING")}
                            </Typography>
                            <Box mx={0.5}>
                                <ChevronRightRoundedIcon color="disabled" />
                            </Box>
                            <Typography variant="h6" color="primary">
                                {tabLabels[activeTab]}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Tab Navigation */}
                <Box gridArea="panel" px={3}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        aria-label="credit reporting tabs"
                        textColor="primary"
                        variant="scrollable"
                        indicatorColor="primary"
                        classes={{
                            indicator: classes.indicator,
                            scrollButtons: classes.scroll,
                        }}
                    >
                        {tabLabels.map((item, index) => (
                            <CustomTab key={index} {...a11yProps(index, item)} />
                        ))}
                    </Tabs>
                    <Divider />
                </Box>

                {/* Tab Content */}
                <SwipeableViews
                    axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                    index={activeTab}
                    onChangeIndex={(index) => onTabChange && onTabChange(index)}
                >
                    <TabPanel value={activeTab} index={0} dir={theme.direction}>
                        {renderExecutiveOverview()}
                    </TabPanel>
                    <TabPanel value={activeTab} index={1} dir={theme.direction}>
                        {renderCreditMovement()}
                    </TabPanel>
                    <TabPanel value={activeTab} index={2} dir={theme.direction}>
                        {renderQCImpactAnalysis()}
                    </TabPanel>
                </SwipeableViews>
            </Box>
        </Wrapper>
    );
}
